import { AssistantFeedbackRating } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';

import type { RootState } from '../store';
import { assistantStore, ChatEntry, getChat, updateChatEntry } from './assistantStore';
import { RecordedStep, startRecording } from './recorder';

/**
 * The designer's feedback on the Assistant, for the team: a rating of a reply, what they'd have wanted it to do, and a
 * recording of them doing it themselves. Each send is posted to Slack with the whole of it attached, so a reply's
 * feedback is sent again as the designer adds to it.
 */

type FeedbackEntry = Extract<ChatEntry, { type: 'feedback' }>;

function getFeedbackEntry(chatId: string, index: number) {
  const entry = getChat(chatId)?.entries[index];
  return entry?.type === 'feedback' ? entry : null;
}

async function send(chatId: string, index: number, changes: Partial<FeedbackEntry>) {
  const chat = getChat(chatId);
  const entry = getFeedbackEntry(chatId, index);
  if (chat == null || entry == null) return;
  const updated: FeedbackEntry = { ...entry, ...changes, status: 'sending' };
  updateChatEntry(chatId, index, updated);

  try {
    await designService.assistant.sendFeedback({
      guid: updated.guid,
      conversationGuid: chat.conversationGuid,
      turnGuid: updated.turnGuid,
      rating: updated.rating,
      comment: updated.comment.trim() === '' ? null : updated.comment.trim(),
      recording: updated.recording,
      context: { request: updated.request, reply: updated.reply, model: chat.model },
    });
    const current = getFeedbackEntry(chatId, index);
    if (current != null) updateChatEntry(chatId, index, { ...current, status: 'sent' });
  } catch (error) {
    console.error(error);
    const current = getFeedbackEntry(chatId, index);
    if (current != null) updateChatEntry(chatId, index, { ...current, status: 'failed' });
  }
}

/** Rates a reply, sending at once so a click is all it takes. Clicking the same rating again clears it. */
export function rateReply(chatId: string, index: number, rating: AssistantFeedbackRating) {
  const entry = getFeedbackEntry(chatId, index);
  if (entry == null) return;
  void send(chatId, index, { rating: entry.rating === rating ? null : rating });
}

/** Sends what the designer would have wanted the Assistant to do, with any recording they made. */
export function sendFeedbackComment(chatId: string, index: number, comment: string) {
  void send(chatId, index, { comment });
}

/**
 * Starts recording the designer doing it themselves. Changes the Assistant makes while a chat runs aren't recorded.
 */
export function startShowMeHow(
  chatId: string,
  index: number,
  store: { getState: () => RootState; subscribe: (listener: () => void) => () => void },
) {
  if (assistantStore.getState().recording != null || getFeedbackEntry(chatId, index) == null) return;
  const addStep = (step: RecordedStep) =>
    assistantStore.setState((state) =>
      state.recording == null ? {} : { recording: { ...state.recording, steps: [...state.recording.steps, step] } },
    );
  const stop = startRecording(store, addStep, () => assistantStore.getState().chats.some((chat) => chat.running));
  assistantStore.setState({
    recording: { chatId, entryIndex: index, startedAt: new Date().toISOString(), steps: [], notes: [], stop },
  });
}

/** Adds what the designer typed while recording, such as why they did a step. */
export function addRecordingNote(text: string) {
  const trimmed = text.trim();
  if (trimmed === '') return;
  assistantStore.setState((state) => {
    if (state.recording == null) return {};
    const at = Date.now() - Date.parse(state.recording.startedAt);
    return { recording: { ...state.recording, notes: [...state.recording.notes, { at, text: trimmed }] } };
  });
}

/** Stops recording and keeps it with the feedback, for the designer to send with their comment. */
export function finishShowMeHow() {
  const recording = assistantStore.getState().recording;
  if (recording == null) return;
  recording.stop();
  // Stopping records the last change, so read the steps after it.
  const { steps, notes, startedAt } = assistantStore.getState().recording ?? recording;
  assistantStore.setState({ recording: null });
  const entry = getFeedbackEntry(recording.chatId, recording.entryIndex);
  if (entry != null)
    updateChatEntry(recording.chatId, recording.entryIndex, {
      ...entry,
      recording: { startedAt, steps, notes },
      status: 'unsent',
    });
}

export function cancelShowMeHow() {
  assistantStore.getState().recording?.stop();
  assistantStore.setState({ recording: null });
}
